import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  notification,
  Radio,
  Row,
  Spin,
} from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { hasValueInList } from '../../helpers/array';
import { formatarCnpjCpfIMask, toTitleCase } from '../../helpers/formatador';
import PermissoesHelper from '../../helpers/PermissoesHelper';
import { Estados } from '../../helpers/uf';
import apiAuth from '../../services/apiAuth';
import apiShipper from '../../services/apiShipper';
import Permissoes from './Permissoes';
import {
  Card,
  CustomItemTitle,
  CustomParagraph,
  DividerLeft,
  DividerRight,
  GlobalStyle,
  WrapperCheckbox,
} from './styles';

function NovoUsuario({ cliente, handleAddNewUser, setEnviandoFormulario }) {
  const SOTRAN = [
    'alternar:login',
    'cadastro-cliente:show',
    'cadastro-usuario:show',
    'dashboard-performance:aderencia-carregamento',
    'dashboard-performance:variacao-frete',
    'lotes:create',
    'lotes:edit',
    'lotes:show',
    'mapa-gerenciamento:show',
    'painel-embarque:contato',
    'painel-embarque:show',
    'painel-embarque:tracking',
    'precificacao:show',
  ];

  const CLIENTE = [
    'painel-embarque:show',
    'painel-embarque:tracking',
    'dashboard-performance:aderencia-carregamento',
    'dashboard-performance:variacao-frete',
    'lotes:show',
    'painel-embarque:contato',
    'mapa-gerenciamento:show',
    'precificacao:show',
    'lotes:edit',
    'lotes:create',
    'alternar:login',
  ];

  const [form] = Form.useForm();

  const [permissoesTipo, setPermissoesTipo] = useState('CLIENTE');
  const [loadingClientesPagadores, setLoadingClientesPagadores] = useState(
    false
  );
  const [loading, setLoading] = useState(false);

  const [clientesPagadores, setClientesPagadores] = useState([]);
  const [selectedClientesPagadores, setSelectedClientesPagadores] = useState(
    []
  );
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [permissions, setPermissions] = useState([]);

  const matchPermissao = (tipo, permissoesData) =>
    permissoesData.reduce((acumulador, permissao) => {
      if (tipo.includes(permissao.rule)) acumulador.push({ id: permissao.id });
      return acumulador;
    }, []);

  useEffect(() => {
    async function onInit() {
      try {
        setLoadingClientesPagadores(true);
        setLoading(true);

        const { data } = await apiShipper.get(
          `/clientes/pagadores?cnpj[]=${cliente.cnpj_resumido}`
        );

        const clientesFilter = data.reduce((clienteLista, clienteCurrent) => {
          const hasCliente = clienteLista.some(
            c => c.cnpj === clienteCurrent.cnpj
          );
          if (!hasCliente) clienteLista.push(clienteCurrent);
          return clienteLista;
        }, []);

        setClientesPagadores(clientesFilter);

        const permissoes = await PermissoesHelper.getPermissoes(false);

        setPermissions(permissoes);

        setSelectedPermissions(matchPermissao(CLIENTE, permissoes));
      } catch (err) {
        notification.error({
          message: 'Ops, não foi possível recuperar os dados do clientes',
          description:
            'Por favor, entre em contato com nosso suporte caso o problema persistir.',
        });
      } finally {
        setLoadingClientesPagadores(false);
        setLoading(false);
      }
    }

    onInit();
  }, [cliente.cnpj_resumido]);

  const handleSelectedAllClientesPagadores = () => {
    if (selectedClientesPagadores.length === clientesPagadores.length) {
      setSelectedClientesPagadores([]);
    } else {
      setSelectedClientesPagadores(clientesPagadores);
    }
  };

  const onSelectPermission = selectedId => {
    const permissoes = hasValueInList(selectedPermissions, 'id', selectedId)
      ? selectedPermissions.filter(({ id }) => id !== selectedId)
      : [...selectedPermissions, { id: selectedId }];

    setSelectedPermissions(permissoes);
  };

  const onSelectClientesPagadores = selectedId => {
    handleSelectedAllClientesPagadores();

    const values = hasValueInList(selectedClientesPagadores, 'id', selectedId)
      ? selectedClientesPagadores.filter(({ id }) => id !== selectedId)
      : [...selectedClientesPagadores, { id: selectedId }];

    setSelectedClientesPagadores(values);
  };

  const onChangePermissaoTipo = event => {
    if (event.target.value === 'SOTRAN') {
      setSelectedPermissions(matchPermissao(SOTRAN, permissions));
    } else if (event.target.value === 'CLIENTE') {
      setSelectedPermissions(matchPermissao(CLIENTE, permissions));
    }
    setPermissoesTipo(event.target.value);
  };

  const handleSubmitUser = async values => {
    if (selectedClientesPagadores.length === 0) {
      notification.error({
        message: 'Ops, não foi possível cadastrar o usuário',
        description: 'Você deve selecionar ao menos um cliente pagador',
      });
      return;
    }

    if (selectedPermissions.length === 0) {
      notification.error({
        message: 'Ops, não foi possível cadastrar o usuário',
        description: 'Você deve selecionar as permissões do usuário',
      });
      return;
    }

    setEnviandoFormulario(true);

    try {
      const valuesToSave = {
        name: values.name,
        email: values.email,
        profile: permissoesTipo,
        cliente_id: cliente.id,
        controle_embarque_estados_permitidos: Estados.map(
          estado => estado.sigla
        ).join(','),
        clientes_pagadores:
          selectedClientesPagadores
            .map(clienteSec => clienteSec.id)
            .toString() || null,
        users_permissions:
          selectedPermissions.map(permission => permission.id) || null,
      };

      const { data } = await apiAuth.post('/users/pre-cadastro', valuesToSave);

      await apiAuth.post(`/users/${data.id}/invite`, {
        razao_social: cliente.razao_social,
      });

      notification.success({
        message: 'Tudo certo',
        description: 'O usuário foi cadastrado com sucesso.',
      });

      handleAddNewUser(false);
    } catch (err) {
      const { error } = err?.response?.data;
      notification.error({
        message: 'Ops, não foi possível cadastrar o usuário',
        description: error?.message,
      });
    } finally {
      setEnviandoFormulario(false);
    }
  };

  return (
    <>
      <GlobalStyle />
      <Row justify="end" className="mb-20">
        <Button Button onClick={handleAddNewUser}>
          Cancelar
        </Button>
      </Row>
      <Card bordered={false}>
        <CustomItemTitle>Dados pessoais</CustomItemTitle>
        <Form
          noValidate
          onFinish={handleSubmitUser}
          layout="vertical"
          form={form}
          autoComplete="off"
        >
          <Row gutter={24}>
            <Col xs={12} lg={10} xl={10}>
              <Form.Item
                name="name"
                label="Nome"
                rules={[
                  {
                    required: true,
                    message: 'Por favor, digite seu nome!',
                  },
                ]}
              >
                <Input className="total-width" />
              </Form.Item>
            </Col>
            <Col xs={12} lg={10} xl={10}>
              <Form.Item
                name="email"
                id="email"
                label="E-mail"
                rules={[
                  {
                    required: true,
                    message: 'Por favor, digite seu e-mail!',
                  },
                ]}
              >
                <Input className="total-width" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={4} xl={4}>
              &nbsp;
            </Col>
          </Row>
          <CustomItemTitle>Permissões</CustomItemTitle>
          <Row gutter={12}>
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <Form.Item name="permissaoTipo">
                <Radio.Group
                  onChange={onChangePermissaoTipo}
                  defaultValue="CLIENTE"
                >
                  <Radio.Button
                    id="radio-button-interno"
                    value="SOTRAN"
                    className={permissoesTipo === 'SOTRAN' ? 'tabAtivo' : ''}
                  >
                    Interno
                  </Radio.Button>
                  <Radio.Button
                    id="radio-button-cliente"
                    value="CLIENTE"
                    className={permissoesTipo === 'CLIENTE' ? 'tabAtivo' : ''}
                  >
                    Cliente
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} lg={24} xl={24} className="mt-20">
              <Permissoes
                selectedPermissions={selectedPermissions}
                onSelectPermission={onSelectPermission}
                isEditing={true}
              />
            </Col>
          </Row>
          <CustomItemTitle withMarginTop>Clientes Pagadores</CustomItemTitle>
          <Row gutter={12}>
            <Col xs={24} lg={24} xl={24}>
              <WrapperCheckbox noMarginTop>
                <DividerLeft>
                  <Checkbox
                    value="selecionar_tudo"
                    id="selecionar_tudo"
                    onChange={handleSelectedAllClientesPagadores}
                    checked={
                      selectedClientesPagadores.length ===
                      clientesPagadores.length
                    }
                  />
                </DividerLeft>
                <DividerRight htmlFor="selecionar_tudo">
                  <CustomParagraph>Selecionar tudo</CustomParagraph>
                </DividerRight>
              </WrapperCheckbox>
              {loadingClientesPagadores && (
                <Spin className="center-loading-spin" message="Carregando..." />
              )}
              {clientesPagadores.map(item => (
                <WrapperCheckbox key={`${item.cnpj}`}>
                  <DividerLeft>
                    <Checkbox
                      value={item.id}
                      id={`cliente-pagador-${item.id}`}
                      onChange={() => onSelectClientesPagadores(item.id)}
                      checked={hasValueInList(
                        selectedClientesPagadores,
                        'id',
                        item.id
                      )}
                    />
                  </DividerLeft>
                  <DividerRight htmlFor={`cliente-pagador-${item.id}`}>
                    <CustomParagraph>
                      CNPJ:&nbsp;
                      {formatarCnpjCpfIMask(item.cnpj)}
                    </CustomParagraph>
                    <CustomParagraph>
                      {`${toTitleCase(item.cidade)} (${item.uf})`}
                    </CustomParagraph>
                  </DividerRight>
                </WrapperCheckbox>
              ))}
            </Col>
          </Row>
          <Row justify="end" className="mt-20">
            <Button loading={loading} type="primary" htmlType="submit">
              Salvar
            </Button>
          </Row>
        </Form>
      </Card>
    </>
  );
}
export default NovoUsuario;

NovoUsuario.propTypes = {
  handleAddNewUser: PropTypes.func.isRequired,
  cliente: PropTypes.objectOf(PropTypes.any),
  setEnviandoFormulario: PropTypes.func.isRequired,
};

NovoUsuario.defaultProps = {
  cliente: {},
};
